// Детайл на анулирано движение — документът какъвто е бил (редовете) + кой/кога,
// с бутон „Възстанови" (връща записа и прилага количествата наново). Видът е
// този на документните модали в модула (икон-чип + заглавие, лека таблица,
// футър с действие вляво и „Затвори" вдясно) — Tailwind-only. Потвърждението
// и заявката за възстановяване са в `useRestoreDeleted` (общи с менюто на реда).
import { Modal } from "antd";
import {
  ArrowRightOutlined,
  CloseOutlined,
  DeleteOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import { modalStyles } from "../../../../utils/modalStyles";
import type { DeletedMovement } from "../../../../api/inventory";
import { fmtDate, fmtDateTime } from "../../components/dateTime";
import { formatQty, useUnitDecimals } from "../../components/qty";
import { formatMoney } from "../../components/numbers";
import { ROW_HEADER_TEXT } from "../../components/list/density";
import { useRestoreDeleted } from "./useRestoreDeleted";

type Props = {
  movement: DeletedMovement | null;
  onClose: () => void;
  typeLabel: (type: string) => string;
  isNarrow: boolean;
};

// Цвят на разликата в инвентаризацията: излишък зелено, липса червено, 0 сиво.
const diffColorClass = (diff: number | null) =>
  diff == null || diff === 0
    ? "text-faint"
    : diff > 0
      ? "text-ok"
      : "text-danger-ink";
const signedColorClass = (value: number) =>
  value < 0
    ? "text-danger-ink"
    : value > 0
      ? "text-ok"
      : "text-ink-2";

const Meta = ({ label, value }: { label: string; value: string }) => (
  <span>
    <span className="mr-1.5 text-[10px] font-medium uppercase leading-[normal] tracking-[0.06em] text-muted">
      {label}
    </span>
    <span className="font-semibold text-ink">{value}</span>
  </span>
);

// Височините са общите (density.ts): заглавие 30px, ред 34px, „Общо" 36px —
// `h-*` на клетка в таблица е най-малката височина на реда
const headerCellClass = `sticky top-0 z-[1] h-[30px] border-b border-shade-2 bg-panel px-3 py-1 ${ROW_HEADER_TEXT}`;
const bodyCellClass = "h-[34px] border-b border-shade-1 px-3 py-1 text-[13px] leading-5";
const numberCellClass = `${bodyCellClass} text-right font-[ui-monospace,Menlo,monospace]`;
const footerLabelClass = `h-9 px-3 py-1 ${ROW_HEADER_TEXT}`;
const footerNumberClass =
  "h-9 px-3 py-1 text-right font-[ui-monospace,Menlo,monospace] text-[13.5px] font-bold leading-[normal]";

export const DeletedDetailModal = ({
  movement,
  onClose,
  typeLabel,
  isNarrow,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useViewport();
  const { requestRestore, isRestoring } = useRestoreDeleted();
  const decimalsOf = useUnitDecimals();
  const width = isMobile ? undefined : isTablet ? "min(860px, 96vw)" : 900;

  const isStocktake = movement?.type === "stocktake";
  const isTransfer = movement?.type === "transfer";

  // Знакова стойност на инвентаризационен ред: излишъкът добавя, липсата вади.
  const signOf = (diff: number | null) => (diff != null && diff < 0 ? -1 : 1);

  const headers = isStocktake
    ? [
        t("inventoryPage.colProductName"),
        t("inventoryPage.colSystem"),
        t("inventoryPage.colCounted"),
        t("inventoryPage.colDiff"),
        t("inventoryPage.colUnitPrice"),
        t("inventoryPage.colNet"),
        t("inventoryPage.colGross"),
      ]
    : [
        t("inventoryPage.colProductName"),
        t("inventoryPage.colWarehouse"),
        t("inventoryPage.colQty"),
        t("inventoryPage.colUnitPrice"),
        t("inventoryPage.colNet"),
        t("inventoryPage.colGross"),
      ];
  const firstNumberColumn = isStocktake ? 1 : 2;

  return (
    <Modal
      open={!!movement}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      {movement && (
        <div className="flex max-h-[92vh] flex-col bg-surface">
          {/* Хедър: вид + дата на документа, кой и кога го е анулирал */}
          <div
            className={`flex shrink-0 items-start justify-between gap-3 border-b border-shade-2 ${
              isMobile ? "px-4 py-3.5" : "px-[22px] py-[18px]"
            }`}
          >
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-brand-tint text-brand-deep-ink">
                <DeleteOutlined className="text-xl" />
              </span>
              <div className="min-w-0">
                <div className="truncate text-[18px] font-bold leading-[1.2] text-ink">
                  {typeLabel(movement.type)} · {fmtDate(movement.date)}
                </div>
                <div className="mt-1 text-[13px] leading-[normal] text-muted">
                  {t("inventoryPage.deletedSubtitle", {
                    who: movement.deletedByName || "—",
                    when: fmtDateTime(movement.deletedAt),
                  })}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("inventoryPage.close")}
              className="flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-muted hover:bg-shade-1"
            >
              <CloseOutlined className="text-sm" />
            </button>
          </div>

          {/* Тяло */}
          <div
            className={`flex min-h-0 flex-col gap-3.5 overflow-y-auto ${
              isMobile ? "p-4" : "px-[22px] py-[18px]"
            }`}
          >
            {/* Мета — склад(ове) / фактура / причина / бележка. При прехвърляне
                показваме изрично ОТ кой КЪМ кой склад е било. */}
            <div className="flex flex-wrap items-center gap-4 text-[13px] text-body">
              {isTransfer ? (
                <span className="inline-flex items-center gap-2">
                  <Meta
                    label={t("inventoryPage.colFromWarehouse")}
                    value={movement.fromWarehouseName || "—"}
                  />
                  <ArrowRightOutlined className="text-[12px] text-faint" />
                  <Meta
                    label={t("inventoryPage.colToWarehouse")}
                    value={movement.toWarehouseName || "—"}
                  />
                </span>
              ) : (
                <Meta
                  label={t("inventoryPage.colWarehouse")}
                  value={movement.warehouseNames.join(", ") || "—"}
                />
              )}
              {movement.invoiceNumber && (
                <Meta
                  label={t("inventoryPage.colInvoice")}
                  value={movement.invoiceNumber}
                />
              )}
              {movement.reason && (
                <Meta
                  label={t("inventoryPage.colReason")}
                  value={movement.reason}
                />
              )}
              {movement.note && (
                <Meta
                  label={t("inventoryPage.colComment")}
                  value={movement.note}
                />
              )}
            </div>

            {/* Редовете на документа */}
            <div className="max-h-[52vh] overflow-auto rounded-xl border border-shade-2">
              <table
                className={`w-full border-collapse ${
                  isStocktake ? "min-w-[720px]" : "min-w-[640px]"
                }`}
              >
                <thead>
                  <tr>
                    {headers.map((label, index) => (
                      <th
                        key={label}
                        className={`${headerCellClass} ${
                          index >= firstNumberColumn ? "text-right" : "text-left"
                        }`}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {movement.lines.map((line, index) => {
                    if (isStocktake) {
                      // Инвентаризация: Търсено/Намерено/Разлика (± с цвят) +
                      // знакова стойност на разликата (излишък +, липса −).
                      const sign = signOf(line.diff);
                      const isZero = line.diff == null || line.diff === 0;
                      return (
                        <tr key={index}>
                          <td
                            className={`${bodyCellClass} font-semibold text-ink`}
                          >
                            {line.productName}
                          </td>
                          <td className={`${numberCellClass} text-ink-2`}>
                            {line.systemQty == null
                              ? "—"
                              : formatQty(line.systemQty, decimalsOf(line.productId))}
                          </td>
                          <td className={`${numberCellClass} text-ink`}>
                            {line.counted == null
                              ? "—"
                              : formatQty(line.counted, decimalsOf(line.productId))}
                            {line.unit ? (
                              <span className="text-muted"> {line.unit}</span>
                            ) : null}
                          </td>
                          <td
                            className={`${numberCellClass} font-bold ${diffColorClass(line.diff)}`}
                          >
                            {line.diff == null
                              ? "—"
                              : line.diff === 0
                                ? "0"
                                : `${line.diff > 0 ? "+" : ""}${formatQty(line.diff, decimalsOf(line.productId))}`}
                          </td>
                          <td className={`${numberCellClass} text-ink-2`}>
                            {formatMoney(line.unitPrice)}
                          </td>
                          <td
                            className={`${numberCellClass} font-semibold ${
                              isZero ? "text-faint" : diffColorClass(line.diff)
                            }`}
                          >
                            {isZero ? "—" : formatMoney(sign * line.sumNoVat)}
                          </td>
                          <td
                            className={`${numberCellClass} font-bold ${
                              isZero ? "text-faint" : diffColorClass(line.diff)
                            }`}
                          >
                            {isZero ? "—" : formatMoney(sign * line.sumWithVat)}
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={index}>
                        <td
                          className={`${bodyCellClass} font-semibold text-ink`}
                        >
                          {line.productName}
                        </td>
                        <td className={`${bodyCellClass} text-ink-2`}>
                          {line.warehouseName || "—"}
                        </td>
                        <td className={`${numberCellClass} text-ink`}>
                          {formatQty(line.quantity, decimalsOf(line.productId))}
                          {line.unit ? (
                            <span className="text-muted"> {line.unit}</span>
                          ) : null}
                        </td>
                        <td className={`${numberCellClass} text-ink-2`}>
                          {formatMoney(line.unitPrice)}
                        </td>
                        <td className={`${numberCellClass} text-ink-2`}>
                          {formatMoney(line.sumNoVat)}
                        </td>
                        <td
                          className={`${numberCellClass} font-bold text-ink`}
                        >
                          {formatMoney(line.sumWithVat)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-panel">
                    <td
                      colSpan={isStocktake ? 5 : 4}
                      className={footerLabelClass}
                    >
                      {isStocktake
                        ? t("inventoryPage.deletedNetValue")
                        : t("inventoryPage.total")}
                    </td>
                    {/* При инвентаризация нетната стойност е знакова: − (липса) в
                        червено, + (излишък) в зелено, 0 — неутрално. */}
                    <td
                      className={`${footerNumberClass} ${
                        isStocktake
                          ? signedColorClass(movement.totalValueNoVat)
                          : "text-ink"
                      }`}
                    >
                      {formatMoney(movement.totalValueNoVat)}
                    </td>
                    <td
                      className={`${footerNumberClass} ${
                        isStocktake
                          ? signedColorClass(movement.totalValueWithVat)
                          : "text-brand-deep-ink"
                      }`}
                    >
                      {formatMoney(movement.totalValueWithVat)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Футър: Възстанови (вляво) · Затвори (вдясно) */}
          <div
            className={`flex shrink-0 items-center justify-between gap-2.5 border-t border-shade-2 ${
              isNarrow ? "px-4 py-3" : "px-[22px] py-3.5"
            }`}
          >
            <button
              type="button"
              onClick={() => requestRestore(movement, onClose)}
              disabled={isRestoring}
              className={`flex h-11 items-center justify-center gap-2 rounded-[10px] border-none bg-brand px-6 text-[13.5px] font-bold leading-[normal] text-white hover:bg-brand-deep ${
                isRestoring
                  ? "cursor-default opacity-70"
                  : "cursor-pointer"
              }`}
            >
              <UndoOutlined className="text-[14px]" />
              {t("inventoryPage.deletedRestore")}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="h-11 cursor-pointer rounded-[10px] border border-shade-3 bg-surface px-[18px] text-[13.5px] font-semibold leading-[normal] text-body hover:bg-fill"
            >
              {t("inventoryPage.close")}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default DeletedDetailModal;
